import { createTheme, Theme } from '@mui/material/styles';

export type ColorMode = 'light' | 'dark';

/**
 * Palette grafite e arancio, coordinata alla consegna licenze Next.
 * Le variabili legacy restano compatibili con tutte le pagine.
 */
type Palette = {
  bgPrimary: string;
  bgSecondary: string;
  bgTertiary: string;
  bgCard: string;
  bgCardHover: string;

  accentBlue: string;
  accentBlueDark: string;
  accentBlueLight: string;
  accentSky: string;
  accentAmber: string;
  accentRose: string;
  accentGreen: string;

  textPrimary: string;
  textSecondary: string;
  textMuted: string;

  border: string;
  borderStrong: string;

  surface: string;
  surfaceAlt: string;
  shadowCard: string;

  appbarBg: string;
  drawerBg: string;
  menuBg: string;

  navActiveBg: string;
  navActiveBgHover: string;
  navActiveBorder: string;
  navActiveText: string;
  navHoverBg: string;
  logoShadow: string;

  bodyBackground: string;

  gradientMain: string;
  gradientAccent: string;
  gradientWarning: string;
  gradientError: string;
};

const dark: Palette = {
  bgPrimary: '#202226',
  bgSecondary: '#292C31',
  bgTertiary: '#36393F',
  bgCard: '#2C2F35',
  bgCardHover: '#373B42',

  accentBlue: '#FF9B38',
  accentBlueDark: '#DF720B',
  accentBlueLight: '#FFB66E',
  accentSky: '#79B7D8',
  accentAmber: '#EDB45E',
  accentRose: '#F18589',
  accentGreen: '#74CB99',

  textPrimary: '#F4F4F5',
  textSecondary: '#C3C4C9',
  textMuted: '#A2A5AD',

  border: 'rgba(255, 255, 255, 0.09)',
  borderStrong: 'rgba(255, 255, 255, 0.20)',

  surface: '#303339',
  surfaceAlt: '#3A3D44',
  shadowCard: '0 3px 12px rgba(2, 8, 20, 0.16)',

  appbarBg: 'rgba(32, 34, 38, 0.94)',
  drawerBg: '#191B1F',
  menuBg: '#36393F',

  navActiveBg: 'rgba(255, 145, 30, 0.13)',
  navActiveBgHover: 'rgba(255, 145, 30, 0.20)',
  navActiveBorder: 'rgba(255, 145, 30, 0.5)',
  navActiveText: '#FFFFFF',
  navHoverBg: 'rgba(255, 255, 255, 0.05)',
  logoShadow: '0 10px 26px rgba(0, 0, 0, 0.22)',

  bodyBackground: [
    'radial-gradient(circle at 0% 0%, rgba(59, 114, 232, 0.10) 0%, transparent 30%)',
    'radial-gradient(circle at 100% 0%, rgba(74, 144, 217, 0.08) 0%, transparent 26%)',
    'linear-gradient(180deg, #0A111E 0%, #0C1422 48%, #0B1220 100%)',
  ].join(', '),

  gradientMain: '#FA8C21',
  gradientAccent: 'linear-gradient(135deg, #79B7D8 0%, #3B72E8 100%)',
  gradientWarning: 'linear-gradient(135deg, #EDB45E 0%, #C9821F 100%)',
  gradientError: 'linear-gradient(135deg, #F18589 0%, #C4424E 100%)',
};

const light: Palette = {
  bgPrimary: '#F4F3F1',
  bgSecondary: '#FFFFFF',
  bgTertiary: '#ECE9E5',
  bgCard: '#FFFFFF',
  bgCardHover: '#F8F6F3',

  accentBlue: '#A94A00',
  accentBlueDark: '#833A00',
  accentBlueLight: '#C86B20',
  accentSky: '#297095',
  accentAmber: '#8B5A08',
  accentRose: '#D14550',
  accentGreen: '#16734C',

  textPrimary: '#24262B',
  textSecondary: '#62646C',
  textMuted: '#6E7078',

  border: 'rgba(50, 42, 30, 0.12)',
  borderStrong: 'rgba(50, 42, 30, 0.23)',

  surface: '#FFFFFF',
  surfaceAlt: '#F8F6F3',
  shadowCard: '0 2px 8px rgba(20, 40, 80, 0.04)',

  appbarBg: 'rgba(255, 255, 255, 0.86)',
  drawerBg: 'rgba(255, 255, 255, 0.97)',
  menuBg: '#FFFFFF',

  navActiveBg: 'rgba(169, 74, 0, 0.08)',
  navActiveBgHover: 'rgba(169, 74, 0, 0.13)',
  navActiveBorder: 'rgba(169, 74, 0, 0.3)',
  navActiveText: '#833A00',
  navHoverBg: 'rgba(20, 40, 80, 0.05)',
  logoShadow: '0 8px 20px rgba(43, 95, 208, 0.26)',

  bodyBackground: [
    'radial-gradient(circle at 0% 0%, rgba(43, 95, 208, 0.06) 0%, transparent 28%)',
    'linear-gradient(180deg, #F1F5FB 0%, #EEF2F8 100%)',
  ].join(', '),

  gradientMain: '#A94A00',
  gradientAccent: 'linear-gradient(135deg, #297095 0%, #2B5FD0 100%)',
  gradientWarning: 'linear-gradient(135deg, #C9821F 0%, #A8690F 100%)',
  gradientError: 'linear-gradient(135deg, #D14550 0%, #B22F3A 100%)',
};

const palettes: Record<ColorMode, Palette> = { dark, light };

export const buildTheme = (mode: ColorMode): Theme => {
  const c = palettes[mode];

  return createTheme({
    palette: {
      mode,
      primary: {
        main: c.accentBlue,
        dark: c.accentBlueDark,
        light: c.accentBlueLight,
        contrastText: mode === 'dark' ? '#24180C' : '#FFFFFF',
      },
      secondary: {
        main: c.accentSky,
        dark: c.accentBlueDark,
        light: c.accentBlueLight,
      },
      info: { main: c.accentSky },
      success: { main: c.accentGreen },
      warning: { main: c.accentAmber },
      error: { main: c.accentRose },
      background: {
        default: c.bgPrimary,
        paper: c.bgCard,
      },
      text: {
        primary: c.textPrimary,
        secondary: c.textSecondary,
      },
      divider: c.border,
    },
    typography: {
      fontSize: 14,
      body1: { fontSize: '0.875rem', lineHeight: 1.65 },
      body2: { fontSize: '0.8125rem', lineHeight: 1.6 },
      caption: { fontSize: '0.72rem', lineHeight: 1.55 },
      fontFamily: '"Manrope", system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
      h1: { fontFamily: '"Space Grotesk", "Manrope", sans-serif', fontWeight: 700, letterSpacing: '-0.03em' },
      h2: { fontFamily: '"Space Grotesk", "Manrope", sans-serif', fontWeight: 700, letterSpacing: '-0.03em' },
      h3: { fontSize: '2rem', fontFamily: '"Space Grotesk", "Manrope", sans-serif', fontWeight: 700, letterSpacing: '-0.02em' },
      h4: { fontSize: '1.75rem', fontFamily: '"Space Grotesk", "Manrope", sans-serif', fontWeight: 700, letterSpacing: '-0.02em' },
      h5: { fontSize: '1.2rem', fontFamily: '"Space Grotesk", "Manrope", sans-serif', fontWeight: 700, letterSpacing: '-0.015em' },
      h6: { fontSize: '1rem', fontFamily: '"Space Grotesk", "Manrope", sans-serif', fontWeight: 700, letterSpacing: '-0.01em' },
      button: { fontFamily: '"Space Grotesk", "Manrope", sans-serif', fontWeight: 700 },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ':root': {
            '--bg-primary': c.bgPrimary,
            '--bg-secondary': c.bgSecondary,
            '--bg-tertiary': c.bgTertiary,
            '--bg-card': c.bgCard,
            '--bg-card-hover': c.bgCardHover,
            '--accent-cyan': c.accentBlue,
            '--accent-blue': c.accentBlue,
            '--accent-sky': c.accentSky,
            '--accent-orange': c.accentAmber,
            '--accent-green': c.accentGreen,
            '--accent-rose': c.accentRose,
            '--text-primary': c.textPrimary,
            '--text-secondary': c.textSecondary,
            '--text-muted': c.textMuted,
            '--border-color': c.border,
            '--border-light': c.borderStrong,
            '--gradient-main': c.gradientMain,
            '--gradient-accent': c.gradientAccent,
            '--surface': c.surface,
            '--surface-alt': c.surfaceAlt,
            '--border': c.border,
            '--shadow-card': c.shadowCard,
            '--appbar-bg': c.appbarBg,
            '--drawer-bg': c.drawerBg,
            '--menu-bg': c.menuBg,
            '--nav-active-bg': c.navActiveBg,
            '--nav-active-bg-hover': c.navActiveBgHover,
            '--nav-active-border': c.navActiveBorder,
            '--nav-active-text': c.navActiveText,
            '--nav-hover-bg': c.navHoverBg,
            '--logo-shadow': c.logoShadow,
          } as any,
          '@keyframes page-enter': { from: { opacity: 0, transform: 'translateY(4px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
          '@media (prefers-reduced-motion: reduce)': { '*': { animationDuration: '0.01ms !important', transitionDuration: '0.01ms !important', scrollBehavior: 'auto !important' } },
          '.MuiButtonBase-root:focus-visible, a:focus-visible': { outline: `2px solid ${c.accentBlue}`, outlineOffset: 3 },
          html: {
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          },
          body: {
            margin: 0,
            backgroundColor: c.bgPrimary,
            color: c.textPrimary,
            backgroundImage: 'none',
            backgroundAttachment: 'fixed',
            minHeight: '100vh',
          },
          a: {
            color: 'inherit',
          },
          '::selection': {
            backgroundColor: mode === 'dark' ? 'rgba(59, 114, 232, 0.40)' : 'rgba(43, 95, 208, 0.22)',
            color: mode === 'dark' ? '#fff' : '#0B1220',
          },
        },
      },
      MuiTextField: { defaultProps: { size: 'small' } },
      MuiFormControl: { defaultProps: { size: 'small' } },
      MuiTabs: { styleOverrides: { root: { minHeight: 44 } } },
      MuiTab: { styleOverrides: { root: { minHeight: 44, textTransform: 'none', fontWeight: 700, fontSize: '0.8125rem' } } },
      MuiMenu: { styleOverrides: { paper: { boxShadow: '0 12px 40px rgba(0,0,0,.14)', borderRadius: 12 } } },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: c.appbarBg,
            borderBottom: `1px solid ${c.border}`,
            backdropFilter: 'blur(16px)',
            boxShadow: 'none',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: c.drawerBg,
            borderRight: `1px solid ${c.border}`,
            backdropFilter: 'blur(12px)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: `1px solid ${c.border}`,
            boxShadow: 'none',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: `1px solid ${c.border}`,
            borderRadius: 12,
            boxShadow: 'none',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 10,
            fontWeight: 700,
            letterSpacing: '0.01em',
            paddingInline: 14,
            minHeight: 38,
            fontSize: '0.8125rem',
          },
          containedPrimary: {
            background: mode === 'dark' ? '#FA8C21' : '#A94A00',
            color: mode === 'dark' ? '#24180C' : '#FFFFFF',
            boxShadow: 'none',
            '&:hover': {
              filter: 'brightness(1.06)',
              boxShadow: mode === 'dark'
                ? '0 8px 22px rgba(0, 0, 0, 0.22)'
                : '0 6px 18px rgba(100, 55, 10, 0.12)',
            },
          },
          outlined: {
            borderColor: c.borderStrong,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            backgroundColor: mode === 'dark' ? 'rgba(0, 0, 0, 0.12)' : '#FFFFFF',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: c.border,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: c.borderStrong,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: c.accentBlue,
              borderWidth: 2,
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            fontWeight: 700,
            borderColor: c.borderStrong,
          },
        },
      },
      MuiDialog: { styleOverrides: { paper: { backgroundColor: mode === 'dark' ? '#44474E' : '#FFFFFF', borderRadius: 16, boxShadow: '0 24px 80px rgba(0,0,0,.35)' } } },
      MuiBackdrop: { styleOverrides: { root: { backgroundColor: 'rgba(8,9,11,.76)' } } },
      MuiDialogTitle: {
        styleOverrides: { root: { fontWeight: 700, padding: '24px 24px 16px' } },
      },
      MuiDialogActions: {
        styleOverrides: { root: { padding: '16px 24px', gap: 8 } },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${c.border}`,
          },
          head: {
            color: c.textSecondary,
            textTransform: 'none',
            letterSpacing: '0.01em',
            fontSize: '0.72rem',
            fontWeight: 700,
            borderBottom: `1px solid ${c.borderStrong}`,
            backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(20, 40, 80, 0.03)',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: mode === 'dark' ? 'rgba(255, 145, 30, 0.055)' : 'rgba(169, 74, 0, 0.04)',
            },
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderColor: c.border,
            color: c.textSecondary,
            '&.Mui-selected': {
              color: mode === 'dark' ? '#fff' : c.accentBlueDark,
              backgroundColor: mode === 'dark' ? 'rgba(255, 145, 30, 0.15)' : 'rgba(169, 74, 0, 0.10)',
              borderColor: c.navActiveBorder,
            },
          },
        },
      },
    },
  });
};

/** Costanti palette (modalità scura) per import legacy. */
export const CM_COLORS = {
  bgPrimary: dark.bgPrimary,
  bgSecondary: dark.bgSecondary,
  bgTertiary: dark.bgTertiary,
  bgCard: dark.bgCard,
  bgCardHover: dark.bgCardHover,
  accentTeal: dark.accentBlue,
  accentBlue: dark.accentBlue,
  accentSky: dark.accentSky,
  accentAmber: dark.accentAmber,
  accentRose: dark.accentRose,
  textPrimary: dark.textPrimary,
  textSecondary: dark.textSecondary,
  textMuted: dark.textMuted,
  border: dark.border,
  borderStrong: dark.borderStrong,
};

export const CM_GRADIENTS = {
  main: dark.gradientMain,
  accent: dark.gradientAccent,
  warning: dark.gradientWarning,
  error: dark.gradientError,
};

const theme = buildTheme('dark');

export default theme;
